import type { ReplayLog } from '@full-elevator/sim';

/**
 * Send a finished run to the playtest collector.
 *
 * The method this project runs on is: play, measure the recording, change the
 * game, measure again. Until now the first arrow in that loop was a person
 * exporting a file on a phone, finding it in Downloads, and attaching it to a
 * message — three manual steps guarding the thing everything else depends on.
 *
 * Three rules, and they are all about not being noticed:
 *
 *   It never blocks.   The results screen is already on screen before this is
 *                      called, and nothing waits for the network.
 *   It never throws.   A failed upload is a missing data point, not a broken
 *                      game. Offline on a train is the normal case, not the
 *                      exception — every session is still in localStorage and
 *                      the export button still works.
 *   It never runs in dev. `__INGEST_TOKEN__` is empty unless CI supplied it, so
 *                      practice runs on a laptop stay out of the record.
 *
 * `keepalive` matters more than it looks: a playtester finishes a level and
 * locks their phone, and without it the request dies with the page.
 */
export function collectReplay(log: ReplayLog): void {
  if (!__INGEST_TOKEN__) return;

  try {
    void fetch('/api/replays', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-ingest-token': __INGEST_TOKEN__,
      },
      body: JSON.stringify(log),
      keepalive: true,
    }).catch(() => {
      // Deliberately silent. There is nothing the player could do about it and
      // nothing the game needs to change because of it.
    });
  } catch {
    // Same again, for the browsers that throw synchronously rather than reject.
  }
}

/**
 * Push everything held locally, for the backlog.
 *
 * A run uploads itself when it ends, but only if there was a network at the
 * time. Somebody who played a whole session on a train has a dozen runs in
 * localStorage and nothing in the collector, so the export button sends the lot
 * as well as downloading it.
 *
 * Resolves to how many the server accepted, or null when there was no way to
 * ask — so the button can say what happened instead of pretending.
 */
export async function collectAll(logs: readonly ReplayLog[]): Promise<number | null> {
  if (!__INGEST_TOKEN__ || logs.length === 0) return null;
  try {
    const response = await fetch('/api/replays', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-ingest-token': __INGEST_TOKEN__ },
      body: JSON.stringify(logs),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { stored?: number };
    return typeof body.stored === 'number' ? body.stored : null;
  } catch {
    return null;
  }
}
